import { ChatEvaluationTestExecutionResultMetrics, useGetApiChatEvaluationTestGet, useGetApiChatEvaluationTestSetGet, useGetApiWorkspaceGet } from "@/API";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";
import { GroupTokenUsageByModel, SumTokenUsageDataWithModel } from "@/Helpers/TokenUsageHelper";
import { ActionIcon, Badge, Box, Button, Divider, Group, NumberInput, Pagination, Paper, SegmentedControl, Stack, Text, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconFilter, IconFilterOff, IconReportAnalytics } from "@tabler/icons-react";
import { useParams } from "@tanstack/react-router";
import { DataTable } from "mantine-datatable";
import { useState } from "react";
import { formatDurationSec, getAggregatedDurations, getAggregatedMetrics, getAggregatedTokenUsages, MetricAggregationType, MetricFilterType } from "./EvaluationHelpers";
import { EvaluationMetricBadges } from "./MetricBadge";
import { TestResultDetailModalContent } from "./TestResultDetailModalContent";
import { ChatEvaluationExportModal } from "./ChatEvaluationExportModal";

export function ChatEvaluationTestPage() {
    const { workspaceId, chatEvaluationTestSetId, chatEvaluationTestId } = useParams({ from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId/" })
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })
    const testSet = useGetApiChatEvaluationTestSetGet({ workspaceId: workspaceId, id: chatEvaluationTestSetId })
    const testResult = useGetApiChatEvaluationTestGet({ workspaceId: workspaceId, id: chatEvaluationTestId })
    const PAGE_SIZE = 10
    const [activePage, setPage] = useState(1)
    const [metricFilter, setMetricFilter] = useState<MetricFilterType>("all")
    const [maxScore, setMaxScore] = useState<number>(0.5)
    const [globalMetricMode, setGlobalMetricMode] = useState<MetricAggregationType>("avg")
    const [selectedRuns, setSelectedRuns] = useState<Record<number, number | "default">>({})
    const [exportModalOpened, setExportModalOpened] = useState(false)

    const results = testResult.data?.data.results ?? []

    const filteredResults = results.filter((item) => {
        if (metricFilter === "all") return true;
        const metrics = getAggregatedMetrics(item.executionResults, globalMetricMode);

        if (metricFilter === "any") {
            return Object.values(metrics).some(m => m?.score != null && m.score <= maxScore);
        }

        const m = metrics[metricFilter];
        return m?.score != null && m.score <= maxScore;
    });

    return <>
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '聊天評估',
                    linkProps: { to: '/workspace/$workspaceId/chatEvaluationTestSet', params: { workspaceId } },
                },
                {
                    label: testSet.data?.data.name ?? "測試資料",
                    linkProps: { to: '/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId', params: { workspaceId, chatEvaluationTestSetId } },
                },
                {
                    label: testResult.data?.data.name ?? "測試結果",
                    active: true,
                }
            ]}
        />
        <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="xs">
            <Group gap="md" align="center" wrap="wrap">
                <Group gap="xs" align="center">
                    <Text size="xs" fw={600} c="dimmed">全域指標模式：</Text>
                    <SegmentedControl
                        size="xs"
                        color="blue"
                        value={globalMetricMode}
                        onChange={(val) => setGlobalMetricMode(val as MetricAggregationType)}
                        data={[
                            { label: "平均", value: "avg" },
                            { label: "最高", value: "max" },
                            { label: "最低", value: "min" },
                        ]}
                    />
                </Group>
                <Divider orientation="vertical" />
                <Group gap="xs" align="center">
                    <Text size="xs" fw={600} c="dimmed">
                        {globalMetricMode === "avg" ? "平均" : globalMetricMode === "max" ? "最高" : "最低"}指標總覽：
                    </Text>
                    <EvaluationMetricBadges metricsList={results.map(x => getAggregatedMetrics(x.executionResults, globalMetricMode))} />
                </Group>
            </Group>
            <Button
                size="xs"
                variant="light"
                color="blue"
                leftSection={<IconReportAnalytics size={16} />}
                onClick={() => setExportModalOpened(true)}
            >
                產出評估報告 / 匯出
            </Button>
        </Group>
        <Paper p="xs" radius="md" withBorder mb="sm" bg="var(--mantine-color-body)">
            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                <Group gap="xs" align="center" wrap="wrap">
                    <Group gap={6}>
                        <IconFilter size={16} color="var(--mantine-color-blue-filled)" />
                        <Text size="xs" fw={600}>指標篩選</Text>
                    </Group>

                    <SegmentedControl
                        size="xs"
                        color="blue"
                        value={metricFilter}
                        onChange={(val) => {
                            setMetricFilter(val as MetricFilterType);
                            setPage(1);
                        }}
                        data={[
                            { label: "全部", value: "all" },
                            { label: "任一指標", value: "any" },
                            { label: "答案準確度", value: "nv_accuracy" },
                            { label: "內容相關性", value: "nv_context_relevance" },
                            { label: "回應根據性", value: "nv_response_groundedness" },
                        ]}
                    />

                    {metricFilter !== "all" && (
                        <Group gap={6} align="center">
                            <Text size="xs" fw={600}>≤</Text>
                            <NumberInput
                                size="xs"
                                w={75}
                                value={maxScore}
                                onChange={(val) => {
                                    const num = typeof val === "number" ? val : (Number.isNaN(parseFloat(val)) ? 0 : parseFloat(val));
                                    setMaxScore(num);
                                    setPage(1);
                                }}
                                min={0}
                                max={1}
                                step={0.05}
                                decimalScale={2}
                            />
                            <Badge variant="light" color={filteredResults.length < results.length ? "orange" : "gray"} size="sm" radius="sm">
                                符合 {filteredResults.length} / {results.length} 筆
                            </Badge>
                            <Tooltip label="重置篩選">
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={() => {
                                        setMetricFilter("all");
                                        setMaxScore(0.5);
                                        setPage(1);
                                    }}
                                >
                                    <IconFilterOff size={14} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    )}
                </Group>

                <Pagination
                    size="sm"
                    value={activePage}
                    onChange={setPage}
                    total={Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE))}
                />
            </Group>
        </Paper>
        <DataTable
            verticalAlign="top"
            withTableBorder
            minHeight={filteredResults.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            fetching={testResult.isFetching}
            columns={[
                {
                    accessor: 'sample.userInput',
                    title: "使用者輸入",
                    resizable: true,
                    width: 250,
                    render: (data) => (
                        <Text size="sm" style={{ wordBreak: "break-word" }}>
                            {data.sample?.userInput}
                        </Text>
                    ),
                },
                {
                    accessor: 'sample.reference',
                    title: "標準回應",
                    resizable: true,
                    width: "50%",
                    render: (data) => (
                        <Text size="sm" style={{ wordBreak: "break-word" }}>
                            {data.sample?.reference}
                        </Text>
                    ),
                },
                {
                    accessor: 'executionResults',
                    title: "AI回應",
                    resizable: true,
                    width: "50%",
                    render: (data) => {
                        const itemIndex = results.indexOf(data);
                        const runCount = data.executionResults?.length ?? 0;
                        const currentRunKey = selectedRuns[itemIndex] ?? "default";

                        let displayResponse = "無回應";
                        if (runCount > 0) {
                            if (currentRunKey === "default" || typeof currentRunKey !== "number") {
                                displayResponse = data.executionResults?.[0]?.response ?? "無回應";
                            } else {
                                displayResponse = data.executionResults?.[currentRunKey]?.response ?? "無回應";
                            }
                        }

                        if (runCount > 1) {
                            return (
                                <Stack gap={4} justify="flex-start" align="flex-start">
                                    <Box onClick={(e) => e.stopPropagation()} w="fit-content">
                                        <SegmentedControl
                                            size="xs"
                                            color="blue"
                                            value={currentRunKey.toString()}
                                            onChange={(val) => {
                                                const nextVal = val === "default" ? "default" : parseInt(val, 10);
                                                setSelectedRuns((prev) => ({
                                                    ...prev,
                                                    [itemIndex]: nextVal,
                                                }));
                                            }}
                                            data={[
                                                { label: "預設", value: "default" },
                                                ...data.executionResults!.map((_, idx) => ({
                                                    label: `#${idx + 1}`,
                                                    value: idx.toString(),
                                                })),
                                            ]}
                                        />
                                    </Box>
                                    <Text size="sm" style={{ wordBreak: "break-word" }}>
                                        {displayResponse}
                                    </Text>
                                </Stack>
                            );
                        }
                        return (
                            <Text size="sm" style={{ wordBreak: "break-word" }}>
                                {displayResponse}
                            </Text>
                        );
                    }
                },
                {
                    accessor: 'responseTime',
                    title: "回應時間",
                    width: 120,
                    resizable: true,
                    render: (data) => {
                        const itemIndex = results.indexOf(data);
                        const currentRunKey = selectedRuns[itemIndex] ?? "default";
                        const executions = data.executionResults ?? [];

                        const runMode = (currentRunKey !== "default" && typeof currentRunKey === "number")
                            ? currentRunKey
                            : globalMetricMode;
                        const { chatDurationMs, evaluationDurationMs } = getAggregatedDurations(executions, runMode);

                        const chatText = formatDurationSec(chatDurationMs);
                        const evalText = formatDurationSec(evaluationDurationMs);
                        const tooltipText = `[對話] ${chatText}\n[評估] ${evalText}`;

                        return (
                            <Tooltip label={tooltipText} multiline style={{ whiteSpace: "pre-line" }}>
                                <Text size="sm">
                                    {chatText}
                                </Text>
                            </Tooltip>
                        );
                    }
                },
                {
                    accessor: 'tokenUsage',
                    title: "Token 使用量",
                    width: 140,
                    resizable: true,
                    render: (data) => {
                        const itemIndex = results.indexOf(data);
                        const currentRunKey = selectedRuns[itemIndex] ?? "default";
                        const executions = data.executionResults ?? [];

                        let activeExecutions = executions;
                        if (currentRunKey !== "default" && typeof currentRunKey === "number" && executions[currentRunKey]) {
                            activeExecutions = [executions[currentRunKey]];
                        }

                        const { chatTokenUsage, evaluationTokenUsage } = getAggregatedTokenUsages(activeExecutions);
                        const chatUsage = SumTokenUsageDataWithModel(chatTokenUsage);
                        const chatGrouped = GroupTokenUsageByModel(chatTokenUsage);
                        const evalGrouped = GroupTokenUsageByModel(evaluationTokenUsage);

                        const lines: string[] = [];
                        chatGrouped.forEach(x => {
                            lines.push(`[對話] ${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`);
                        });
                        evalGrouped.forEach(x => {
                            lines.push(`[評估] ${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`);
                        });

                        const tooltipText = lines.join('\n') || "無 Token 紀錄";

                        return (
                            <Tooltip label={tooltipText} multiline style={{ whiteSpace: "pre-line" }}>
                                <Text size="sm">
                                    {chatUsage.inputTokens ?? 0} / {chatUsage.outputTokens ?? 0}
                                </Text>
                            </Tooltip>
                        );
                    }
                },
                {
                    accessor: "metrics",
                    width: 200,
                    title: "指標",
                    resizable: true,
                    render: (data) => {
                        const itemIndex = results.indexOf(data);
                        const currentRunKey = selectedRuns[itemIndex] ?? "default";
                        const executions = data.executionResults ?? [];

                        let metricsData: ChatEvaluationTestExecutionResultMetrics = {};
                        if (currentRunKey === "default" || typeof currentRunKey !== "number") {
                            metricsData = getAggregatedMetrics(executions, globalMetricMode);
                        } else if (executions[currentRunKey]?.metrics) {
                            metricsData = executions[currentRunKey].metrics!;
                        }

                        return (
                            <Group>
                                <EvaluationMetricBadges metricsList={[metricsData]} />
                            </Group>
                        );
                    }
                },
            ]}
            onRowDoubleClick={({ record }) => {
                const itemIndex = results.indexOf(record);
                const currentRunKey = selectedRuns[itemIndex] ?? "default";

                modals.open({
                    title: '詳細測試結果',
                    size: "xl",
                    children: <TestResultDetailModalContent record={record} initialRunIndex={currentRunKey} globalMetricMode={globalMetricMode} />,
                });
            }}
            records={filteredResults.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE)}
        />
        <ChatEvaluationExportModal
            opened={exportModalOpened}
            onClose={() => setExportModalOpened(false)}
            workspaceId={workspaceId}
            chatEvaluationTestSetId={chatEvaluationTestSetId}
            chatEvaluationTestId={chatEvaluationTestId}
            defaultTitle={testResult.data?.data.name ? `${testResult.data.data.name} - AI 智慧問答成效評估報告` : "AI 智慧問答成效評估報告"}
            defaultCustomerName={workspace.data?.data.name || ""}
            rawResults={results}
            currentMode={globalMetricMode}
        />
    </>
}
